import React, {useState, useCallback, useEffect} from 'react';
import { set, useForm } from 'react-hook-form';
import { useQuery, useMutation } from '@apollo/client';
import DataState from 'app/src/components/DataState';
import Input from 'app/src/elements/inputs';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import { View, Text, Pressable } from 'react-native';
import SubNav from './SubNav';
import { useNavigate } from '../../utils/routing';
import { ASSET, UPDATE_ASSET, EXISTING_ASSETS } from './queries';
import { AssetLeftContainer, AssetRightContainer, ButtonContainer } from './elements';
import { useParams } from 'react-router-dom';
import { AssetsContainer } from './elements';
import moment from 'moment';
import {
  ContentContainer,
  MainContainer,
  TitleContainer,
  ContainerTitle
} from '../../elements';
import Modal from 'app/src/elements/Modal';
import Photos from '../Attachments';
import Documents from '../Attachments';
import NewAttachment from '../Attachments/new';
import { AddAttachmentButton } from '../Attachments/elements';
import Autocomplete from '../Autocomplete';
import { formatAddress } from '../../utils/formatAssetName';
import AssignToUsers from './AssignToUsers';
import CustomLocation from '../Locations/CustomLocation';
import elRed from '../../styles/colors';

const URL = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/

const EditAsset = () => {
  const { sellerId, assetId } = useParams();
  const [asset, setAsset] = useState();
  const [submitting, setSubmitting] = useState();
  const [showAddPhotoModal, setShowAddPhotoModal] = useState(false);
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedSellerUsers, setSelectedSellerUsers] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [showCustomLocationFields, setShowCustomLocationFields] = useState(false);
  const [existingVins, setExistingVins] = useState([]);
  const [showDuplicateVin, setShowDuplicateVin] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const navigate = useNavigate();
  const [updateAsset] = useMutation(UPDATE_ASSET, {
    onCompleted: () => {
      navigate(`/sellers/${sellerId}/assets/${assetId}`)
    }
  });

  const onSubmit = useCallback(input => {
    setSubmitting(true);
    input['year'] = parseInt(input['year']);
    input['miles'] = input['miles'] ? parseInt(input['miles']) : null;
    if(input['acquiredOn']) {
      input['acquiredOn'] = moment(input['acquiredOn']).format('YYYY-MM-DD');
    } else {
      input['acquiredOn'] = null;
    }
    if(input['securedOn']) {
      input['securedOn'] = moment(input['securedOn']).format('YYYY-MM-DD');
    } else {
      input['securedOn'] = null;
    }
    input['customLocation'] = showCustomLocationFields;
    input['userIds'] = selectedUsers;
    input['sellerUserIds'] = selectedSellerUsers;
    if (['Select', ''].includes(input['mechanicalCondition'])) {
      input['mechanicalCondition'] = null;
    }
    if(input['uccExpiresOn'] === "") {
      input['uccExpiresOn'] = null;
    }
    if(input['reservePrice'] === "") {
      input['reservePrice'] = null;
    }
    updateAsset({ variables: input });
  });

  const { loading, error, data, refetch } = useQuery(
    ASSET, { variables: { id: assetId } }
  );

  const { data: existingData } = useQuery(EXISTING_ASSETS);

  useEffect(() => {
    if(existingData) {
      let vins = existingData.assets.map(asset => asset.vin);
      setExistingVins(vins);
    }
  }, [existingData])

  const { control, handleSubmit, setValue, reset, resetField, watch, register,
    formState: { errors }
  } = useForm({
    defaultValues: {
      id: assetId,
      sellerId: sellerId,
      year: parseInt(moment().format('YYYY')),
      make: '',
      model: '',
      vin: '',
      stockNumber: '',
      clientNumber: '',
      mechanicalCondition: '',
      miles: '',
      reservePrice: '',
      expectedYieldPrice: '',
      inspectionUrl: '',
      inspectionCompleted: data?.asset.nisInspectionCompleted || false,
      nisInspectionUrl: '',
      nisInspectionCompleted: data?.asset.nisInspectionCompleted || false,
      securedOn: data?.asset.securedOn || '',
      acquired: '',
      acquiredOn: '',
      acquiredPrice: '',
      acquiredExpenses: '',
      noTitleReminders: '',
      feeType: 'Seller',
      feeAmount: '0',
      street: data?.asset.street || '',
      city: data?.asset.city || '',
      state: data?.asset.state || '',
      postalCode: data?.asset.postalCode.formatted || '',
      releaseYardEmail: data?.asset.releaseYardEmail || '',
      releaseYardContact: data?.asset.releaseYardContact || '',
      equiplincContactId: data?.asset.equiplincContact?.id,
      featured: data?.asset.featured || false,
      newArrival: data?.asset.newArrival || false,
      showOnWeb: data?.asset.showOnWeb || false,
      uccExpiresOn: data?.asset.uccExpiresOn || '',
      titleApplicableAsset: data?.asset.titleApplicableAsset || 'Title'
    }
  });

  const acquired = watch('acquired');
  const watchFeeType = watch('feeType');
  const watchVin = watch('vin');
  const watchDrivable = watch('drivable');
  const watchFeatured = watch('featured');
  const watchShowListPrice = watch('showListPrice');
  const watchInspectionUrl = watch('inspectionUrl');

  const checkVin = () => {
    if(existingVins && watchVin) {
      return existingVins.includes(watchVin) && watchVin !== data?.asset?.vin;
    }
  }

  useEffect(() => {
    if(watchVin) {
      let exists = checkVin();
      if(exists) {
        if(window.confirm('An Asset with this VIN already exists within the ' +
          'system. Do you wish to duplicate?'
        )) {

        }else {
          resetField('vin', '');
        }
        setShowDuplicateVin(true);
      }else {
        setShowDuplicateVin(false);
      }
    }else {
      setShowDuplicateVin(false);
    }
  }, [watchVin])

  useEffect(() => {
    if (watchInspectionUrl &&
      data && data.asset.inspectionUrl !== watchInspectionUrl
    ) {
      setValue('inspectionCompleted', true);
    }
  }, [watchInspectionUrl]);

  useEffect(() => {
    if(data !== undefined && asset === undefined) {
      setAsset(data?.asset);
      setPhotos(data?.asset?.photos);
      setDocuments(data?.asset?.documents);
      setShowCustomLocationFields(data?.asset?.customLocation);
      setLocationId(data?.asset.locationId);
      setStreet(data?.asset.street);
      setCity(data?.asset.city);
      setState(data?.asset.state);
      setPostalCode(data?.asset.postalCode.formatted);
      reset(
        {
          id: assetId,
          sellerId: sellerId,
          year: data?.asset?.year || parseInt(moment().format('YYYY')),
          make: data?.asset?.make || '',
          model: data?.asset?.model || '',
          vin: data?.asset?.vin || '',
          stockNumber: data?.asset.stockNumber || '',
          clientNumber: data?.asset.clientNumber || '',
          mechanicalCondition: data?.asset.mechanicalCondition || '',
          miles: data?.asset.miles || '',
          expectedYieldPrice: data?.asset.expectedYieldPrice || '',
          reservePrice: data?.asset?.activeSaleAsset?.reservePrice ||
            data?.asset?.reservePrice || '',
          inspectionUrl: data?.asset.inspectionUrl || '',
          inspectionCompleted: data?.asset.inspectionCompleted || false,
          nisInspectionUrl: data?.asset.nisInspectionUrl || '',
          nisInspectionCompleted: data?.asset.nisInspectionCompleted || false,
          securedOn: data?.asset.securedOn || '',
          locationId: data?.asset.location?.id || '',
          acquired: data?.asset.acquired || '',
          acquiredOn: data?.asset.acquiredOn || '',
          acquiredPrice: data?.asset.acquiredPrice || '',
          acquiredExpenses: data?.asset.acquiredExpenses || '',
          noTitleReminders: data?.asset.noTitleReminders || '',
          feeType: data?.asset.feeType || 'Seller',
          feeAmount: data?.asset.feeAmount || '0',
          street: data?.asset.street || '',
          city: data?.asset.city || '',
          state: data?.asset.state || '',
          postalCode: data?.asset.postalCode.formatted || '',
          releaseYardEmail: data?.asset.releaseYardEmail || '',
          releaseYardContact: data?.asset.releaseYardContact || '',
          equiplincContactId: data?.asset.equiplincContact?.id,
          featured: data?.asset.featured || false,
          showOnWeb: data?.asset.showOnWeb || false,
          drivable: data?.asset.drivable || false,
          showListPrice: data?.asset.showListPrice || false,
          listPrice: parseFloat(data?.asset.listPrice) || '',
          uccExpiresOn: data?.asset.uccExpiresOn || '',
          titleApplicableAsset: data?.asset.titleApplicableAsset || 'Title'
        }
      );

      setSelectedUsers(data?.asset.assetUsers.map(
        assetUser => assetUser.user.id
      ));
      setSelectedSellerUsers(data?.asset.assetSellerUsers.map(
        assetUser => assetUser.sellerUser.id
      ));
    }
  }, [data])

  return (
    <MainContainer>
      {loading ||!data?.asset ?
        <DataState.Loading /> :
        <View>
          <SubNav
            assetId={assetId}
            sellerId={sellerId}
            sellerName={asset?.seller?.name}
            vin={asset?.vin}
          />
          <ContentContainer>
            <AssetsContainer>
              <AssetLeftContainer>
                <TitleContainer>
                  <ContainerTitle>
                    Asset Details
                  </ContainerTitle>
                </TitleContainer>
                {submitting ?
                  DataState.Loading :
                  <View>
                    <Input.Switch
                      label="Launch on Buyer Website?"
                      name="showOnWeb"
                      control={control}
                      errors={errors}
                    />
                    <Input.Switch
                      label="Featured?"
                      name="featured"
                      control={control}
                      errors={errors}
                    />
                    <View style={{display:'flex', flexDirection: 'row'}}>
                      <Input.Switch
                        label="New Arrival?"
                        name="newArrival"
                        control={control}
                        errors={errors}
                        disabled={data?.asset?.activeSaleAsset?.lotNumber}
                      />
                      {data?.asset?.activeSaleAsset?.lotNumber &&
                        <Text style={{color: 'red', margin: '7.5px'}}>
                          Asset has an assigned Lot Number
                        </Text>
                      }
                    </View>
                    {watchFeatured &&
                      <Input.Switch
                        label="Show Buy Now Price?"
                        name="showListPrice"
                        control={control}
                        errors={errors}
                      />
                    }
                    {watchFeatured && watchShowListPrice &&
                      <Input.Currency
                        label="Buy Now Price:"
                        name="listPrice"
                        control={control}
                        errors={errors}
                        {...register("listPrice", {
                          valueAsNumber: true,
                          pattern:{
                             value: /^(0|[1-9]\d*)(\.\d+)?$/
                          },
                        })}
                      />
                    }
                    <Input.YearPicker
                      label="Year:"
                      name="year"
                      control={control}
                      errors={errors}
                    />
                    {/* <Input.Text
                      label="Make:"
                      name="make"
                      control={control}
                      errors={errors}
                    />
                    <Input.Text
                      label="Model:"
                      name="model"
                      control={control}
                      errors={errors}
                    /> */}
                    <Autocomplete
                      label={'Make'}
                      placeHolder={''}
                      resource={'InputOption'}
                      setValue={setValue}
                      name='make'
                      zIndex={2}
                      defaultValue={
                        asset?.make ?
                        {
                          __typename: 'InputOption',
                          type: 'make', label: asset.make
                        } :
                        ''
                      }
                      {...register('make', { required: 'Required' })}
                    />
                    <Autocomplete
                      label={'Model'}
                      placeHolder={''}
                      resource={'InputOption'}
                      setValue={setValue}
                      name='model'
                      zIndex={1}
                      defaultValue={
                        asset?.model ?
                        {
                          __typename: 'InputOption',
                          type: 'model', label: asset.model
                        } :
                        ''
                      }
                      {...register('model', { required: 'Required' })}
                    />
                    <Input.Text
                      label="VIN:"
                      name="vin"
                      control={control}
                      errors={errors}
                      {...register('vin', {
                        maxLength: { value: 17, message: 'Max 17 characters' }
                      })}
                    />
                    {showDuplicateVin &&
                      <View style={{marginTop: '-10px', marginBottom: '10px'}}>
                        <Text style={{fontWeight: 'bold', color: elRed}}>
                          Warning: Asset already exists within system!
                        </Text>
                      </View>
                    }
                    <Input.Text
                      label="Stock Number:"
                      name="stockNumber"
                      control={control}
                      errors={errors}
                    />
                    <Input.Text
                      label={`${data?.asset.seller.clientNumberLabelFull}:`}
                      name="clientNumber"
                      control={control}
                    />
                    <Input.Text
                      label="Odometer:"
                      name="miles"
                      control={control}
                      errors={errors}
                    />
                    <Input.Picker
                      label="Mechanical Condition:"
                      name="mechanicalCondition"
                      control={control}
                      options={
                        ['Runs', 'Inoperable'].map(option => {
                          return {value: option, label: option}
                        })
                      }
                    />
                    <Input.Text
                      label="Expected Yield Price:"
                      name="expectedYieldPrice"
                      control={control}
                    />
                    <Input.Text
                      label="Reserve Price:"
                      name="reservePrice"
                      control={control}
                    />
                    <Input.Text
                      label="Inspection URL:"
                      name="inspectionUrl"
                      control={control}
                      errors={errors}
                      {...register('inspectionUrl', {
                        pattern: { value: URL, message: 'Must be a valid URL' }
                      })}
                    />
                    <Input.Switch
                      label="Inspection Completed?"
                      name="inspectionCompleted"
                      control={control}
                      errors={errors}
                    />
                    <Input.Text
                      label="NIS Inspection URL:"
                      name="nisInspectionUrl"
                      control={control}
                      errors={errors}
                      {...register('nisInspectionUrl', {
                        pattern: { value: URL, message: 'Must be a valid URL' }
                      })}
                    />
                    <Input.Switch
                      label="NIS Inspection Completed?"
                      name="nisInspectionCompleted"
                      control={control}
                      errors={errors}
                    />
                    <Input.Date
                      label="Date Secured:"
                      name="securedOn"
                      control={control}
                      errors={errors}
                    />
                    <Input.Text
                      label="To mute missing title reminders, provide a note:"
                      name="noTitleReminders"
                      control={control}
                    />
                    {asset?.seller?.isEquiplinc &&
                      <View>
                        <Input.Picker
                          label="Acquired?"
                          name="acquired"
                          control={control}
                          errors={errors}
                          options={
                            ["No", "Inherited", "Purchased"].map(option => {
                              return {value: option, label: option}
                            })
                          }
                        />
                        {['Inherited', 'Purchased'].includes(acquired) && (
                          <View style={{flexDirection: 'row', gap: 10}}>
                            <Input.Date
                              label="Acquired Date:"
                              name="acquiredOn"
                              control={control}
                              errors={errors}
                            />
                            <Input.Text
                              label="Acquired Price:"
                              name="acquiredPrice"
                              control={control}
                            />
                            <Input.Text
                              label="Expenses:"
                              name="acquiredExpenses"
                              control={control}
                            />
                          </View>
                        )}
                      </View>
                    }
                    {!showCustomLocationFields &&
                      <Autocomplete
                        label={'Location'}
                        placeHolder={
                          asset?.location ? formatAddress(asset?.location) : ''
                        }
                        resource={'Location'}
                        setValue={setValue}
                        name='locationId'
                        defaultValue={asset?.location || ''}
                      />
                    }
                    <View>
                      {!showCustomLocationFields &&
                        <Pressable
                          onPress={() => setShowCustomLocationFields(true)}
                        >
                          <Text
                            style={{
                              fontSize: '14px',
                              marginTop: '-5px',
                              marginLeft: '10px'
                            }}
                          >
                            Custom Location
                          </Text>
                        </Pressable>
                      }
                    </View>
                    {showCustomLocationFields &&
                      <CustomLocation
                        setShowCustomLocationFields={setShowCustomLocationFields}
                        register={register}
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        asset={asset}
                      />
                    }
                    <br />
                    <Input.Checkbox
                      label="Is Asset Drivable?"
                      name="drivable"
                      checked={watchDrivable}
                      control={control}
                      errors={errors}
                    />
                    <Input.Picker
                      label="Sell Fee Type:"
                      name="feeType"
                      control={control}
                      errors={errors}
                      options={[
                        { value: 'Seller', label: 'Seller Default' },
                        { value: 'Percent', label: 'Percent' },
                        { value: 'Flat Fee', label: 'Flat Fee' }
                      ]}
                      {...register('feeType', { required: 'Required' })}
                    />
                    {'Seller' !== watchFeeType && (
                      <Input.Number
                        label={
                          `Sell Fee ${'Flat Fee' === watchFeeType ? 'Amount' : '%'}:`
                        }
                        name="feeAmount"
                        control={control}
                        errors={errors}
                      />
                    )}
                    <Input.Date
                      label="Eligible Sale Date: "
                      name="uccExpiresOn"
                      control={control}
                      errors={errors}
                    />
                    <Input.Picker
                      label="Which Title Release Process does this Asset Follow?"
                      name="titleApplicableAsset"
                      control={control}
                      errors={errors}
                      options={
                        ["Title", "BOS Only"].map(option => {
                          return {value: option, label: option}
                        })
                      }
                    />
                    <AssignToUsers
                      sellerId={sellerId}
                      selectedUsers={selectedUsers}
                      setSelectedUsers={setSelectedUsers}
                      selectedSellerUsers={selectedSellerUsers}
                      setSelectedSellerUsers={setSelectedSellerUsers}
                    />
                    <ButtonContainer>
                      <ButtonGroup>
                        <Button label="Update Asset" onPress={handleSubmit(onSubmit)} />
                      </ButtonGroup>
                    </ButtonContainer>
                  </View>
                }
              </AssetLeftContainer>
              <AssetRightContainer>
                <TitleContainer>
                  <ContainerTitle>
                    <Text>Asset Images</Text>
                  </ContainerTitle>
                </TitleContainer>
                <AddAttachmentButton
                  onPress={() => setShowAddPhotoModal(true)}
                >
                  <Text>
                    + Add Asset Photo
                  </Text>
                </AddAttachmentButton>
                <Photos
                  indexType={'photos'}
                  attachments={photos}
                  updateAttachments={updateAttachments}
                />
                <br />
                <hr />
                <br />
                <TitleContainer>
                  <ContainerTitle>
                    <Text>Asset Documents</Text>
                  </ContainerTitle>
                </TitleContainer>
                <AddAttachmentButton
                  onPress={() => setShowAddDocumentModal(true)}
                >
                  <Text>
                    + Add Asset Document
                  </Text>
                </AddAttachmentButton>
                <Documents
                  indexType={'documents'}
                  attachments={documents}
                  updateAttachments={updateAttachments}
                />
              </AssetRightContainer>
            </AssetsContainer>
          </ContentContainer>
        </View>
      }
      {showAddPhotoModal &&
        <Modal
          showModal={showAddPhotoModal}
          setShowModal={setShowAddPhotoModal}
          modalType={'Add Photo'}
        >
          <NewAttachment
            setShowModal={setShowAddPhotoModal}
            recordId={assetId}
            recordType='Asset'
            attachmentType='Photo'
            updateAttachments={updateAttachments}
          />
        </Modal>
      }
      {showAddDocumentModal &&
        <Modal
          showModal={showAddDocumentModal}
          setShowModal={setShowAddDocumentModal}
          modalType={'Add Document'}
        >
          <NewAttachment
            setShowModal={setShowAddDocumentModal}
            recordId={assetId}
            recordType='Asset'
            attachmentType='Document'
            updateAttachments={updateAttachments}
          />
        </Modal>
      }
    </MainContainer>
  )

  function updateAttachments(attachmentType, action, newAttachment) {
    let newAttachments = attachmentType === 'Photo' ? photos : documents;
    if(action === 'add') {
      newAttachments = [...newAttachments, newAttachment];
    }else {
      newAttachments = newAttachments.filter(attachment =>
        parseInt(attachment.id) !== parseInt(newAttachment.id)
      )
    }
    attachmentType === 'Photo' ?
      setPhotos(newAttachments) :
      setDocuments(newAttachments);
  }
}

export default EditAsset;
